import { Button } from '@insify/design-system/components';
import { IconicBook, IconicDocumentShield } from '@insify/design-system/iconic-icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useGetPolicyDocument } from '../../api/queries/useGetPolicyDocument';
import { GTMEvent, useTagManager } from '../../hooks/useTagManager';

import * as styles from './DocumentDownloadButtons.module.css';

import type { Coverage } from '../../types/Policy';

export const DocumentDownloadButtons: React.FC<{
  coverage: Coverage;
  policyType: 'personal' | 'company';
}> = ({ coverage, policyType }) => {
  const { t } = useTranslation('policy');
  const { sendEvent } = useTagManager();

  return policyType === 'personal' ? (
    coverage.documents &&
      coverage?.documents.map((document) => {
        return (
          <Button
            key={document.displayName}
            trackingId='downloadDocument'
            type='button'
            kind='primary'
            className={clsx(styles.button, styles.fullWidth)}
            onClick={() => {
              sendEvent(GTMEvent.POLICY_DOWNLOAD, { coverage: coverage.title, document: document.type });
              window.open(document.url, '_blank');
            }}
          >
            {t('download_policy_document')}
          </Button>
        );
      })
  ) : (
    <div className={styles.buttonGroup}>
      <ProgressiveDownloadButton
        kind='neutral'
        url={coverage.scheduleURL}
        trackingId='downloadSchedule'
        titleKey='download_schedule'
        documentTitle={coverage.title}
        documentType='schedule'
        leftIcon={<IconicDocumentShield />}
      />
      <ProgressiveDownloadButton
        kind='neutral'
        url={coverage.wordingsURL}
        trackingId='downloadWordings'
        titleKey='download_wordings'
        documentTitle={coverage.title}
        documentType='conditions'
        leftIcon={<IconicBook />}
      />
    </div>
  );
};

export const ProgressiveDownloadButton: React.FC<{
  url?: string;
  kind: 'primary' | 'secondary' | 'neutral';
  titleKey: string;
  documentType: 'schedule' | 'conditions';
  documentTitle: string;
  leftIcon?: React.ReactElement;
  trackingId: string;
}> = ({ url, titleKey, documentTitle, documentType, ...props }) => {
  const { t } = useTranslation('policy');
  const { sendEvent } = useTagManager();

  const { isError, isPending, mutate: openDocument } = useGetPolicyDocument(url ?? '');

  const onClick = () => {
    sendEvent(GTMEvent.POLICY_DOWNLOAD, { coverage: documentTitle, document: documentType });
    openDocument();
  };

  return (
    <Button
      type='button'
      className={styles.button}
      onClick={onClick}
      loading={isPending}
      disabled={!url || isError}
      {...props}
    >
      {isError ? t('document_unavailable') : t(titleKey)}
    </Button>
  );
};
